import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
    Alert,
    Badge,
    Chip,
    Tab,
    Tabs,
} from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import Layout from "../../Components/Layout";
import IndexApp from "../../Components/IndexApp";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import ButtonField from "../../Components/Ui/ButtonField";
import config from "../../config/config";
import {
    ENV_DECLARONS,
    getActiveBrandConfig,
    getActiveClientBrandConfig,
    getActivePrestataireBrandConfig,
    getActivePrestationBrandConfig,
    isBrandActive,
} from "../../config/environment.config";
import { capitalizeFirstLetter } from "../../Utils/Function";
import TravelRepository, { ComplianceFlagAdmin } from "../../Repositories/TravelRepository";

type ReviewCourse = {
    id: number;
    declarationStatus: string;
    declarationStatusReason: string | null;
    startDatetime: string | null;
    duration: string | null;
    travelDistanceKm: string | null;
    teachr: { id: number; firstName: string; lastName: string; mail: string } | null;
    client: { id: number; firstName: string; lastName: string } | null;
    address: string | null;
    teachrAddress: string | null;
    travelProof: { path: string; url: string } | null;
    attestation: {
        url: string;
        signedAt: string | null;
        travelModeLabel: string;
        distanceKm: string;
        supportingDocs: Array<{ path: string; url: string }>;
    } | null;
};

/**
 * Interface admin – revue des déclarations soumises aux règles de distance :
 * - onglet « À approuver » (> 160 km) : détails + preuve + Approuver / Demander un justificatif (rejet avec motif) / Refuser (annulation)
 * - onglet « À vérifier » (60–160 km) : attestation + justificatifs + Valider
 * L'accès est contrôlé côté backend (ROLE_ADMIN) ; côté front la page n'est
 * rendue que pour le compte admin configuré.
 */
const AdminDeclarationReviewPage = () => {
    const { classes, common } = useTailwindBrand();
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const [tab, setTab] = useState(0);
    const [toApprove, setToApprove] = useState<ReviewCourse[]>([]);
    const [toVerify, setToVerify] = useState<ReviewCourse[]>([]);
    // P7 : anomalies mode de déplacement / distance (jamais bloquantes), à résoudre ou ignorer
    const [flags, setFlags] = useState<ComplianceFlagAdmin[]>([]);
    const [flagsError, setFlagsError] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionId, setActionId] = useState<number | null>(null);

    // Mots de marque : cours/prestation (féminin pour Déclarons), élève/client, professeur/coach…
    const prestationWord = getActivePrestationBrandConfig();
    const fem = isBrandActive(ENV_DECLARONS) ? "e" : "";
    const lePrestation = getActivePrestationBrandConfig("", "le ", "la ");
    const prestataireWord = getActivePrestataireBrandConfig();

    const loadReviews = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await TravelRepository.getDeclarationReviews();
            setToApprove(data.toApprove ?? []);
            setToVerify(data.toVerify ?? []);
            // l'onglet Anomalies se charge à part : son échec ne masque pas les deux autres files
            try {
                const flagData = await TravelRepository.getComplianceFlags("active");
                setFlags(flagData.flags ?? []);
                setFlagsError("");
            } catch (e) {
                setFlags([]);
                setFlagsError("Impossible de charger les anomalies pour le moment.");
            }
        } catch (e) {
            setError("Impossible de charger les déclarations à revoir. Vérifiez vos droits d'accès.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    if (teachr && teachr.id !== config.adminId) {
        return <Navigate to={`/${getActiveBrandConfig()}/tableau-de-bord`} replace />;
    }

    const handleApprove = async (course: ReviewCourse) => {
        const { isConfirmed } = await Swal.fire({
            icon: "question",
            title: `Approuver la déclaration n°${course.id} ?`,
            html: `${capitalizeFirstLetter(lePrestation)} sera déclaré${fem} et suivra le circuit habituel de transmission à l'Urssaf.<br/>Un email de confirmation sera envoyé au ${prestataireWord} et à l'adresse contact.`,
            showCancelButton: true,
            confirmButtonText: "Oui, approuver",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#0054A3",
        });
        if (!isConfirmed) return;
        setActionId(course.id);
        try {
            await TravelRepository.approveCourse(course.id);
            await loadReviews();
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erreur", text: "L'approbation a échoué." });
        } finally {
            setActionId(null);
        }
    };

    const handleReject = async (course: ReviewCourse) => {
        const { value: reason, isConfirmed } = await Swal.fire({
            icon: "warning",
            title: `Demander un nouveau justificatif pour la déclaration n°${course.id} ?`,
            text: `${capitalizeFirstLetter(lePrestation)} reste non déclaré${fem} tant que le ${prestataireWord} n'a pas envoyé un nouveau justificatif. Il sera prévenu par email et dans ses notifications, avec le motif. Une copie de confirmation part à l'adresse contact.`,
            input: "textarea",
            inputLabel: `Motif (communiqué au ${prestataireWord})`,
            inputPlaceholder: "Ex : la preuve de déplacement fournie est illisible…",
            inputValidator: (value) => (!value || !value.trim() ? "Merci d'indiquer le motif du rejet." : null),
            showCancelButton: true,
            confirmButtonText: "Oui, demander un justificatif",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#d33",
        });
        if (!isConfirmed) return;
        setActionId(course.id);
        try {
            await TravelRepository.rejectCourse(course.id, reason || undefined);
            await loadReviews();
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Le rejet a échoué." });
        } finally {
            setActionId(null);
        }
    };

    const handleRefuse = async (course: ReviewCourse) => {
        const { value: reason, isConfirmed } = await Swal.fire({
            icon: "warning",
            title: `Refuser définitivement la déclaration n°${course.id} ?`,
            html: `${capitalizeFirstLetter(lePrestation)} sera <b>annulé${fem}</b> : il disparaît des listes du ${prestataireWord} et ne sera jamais transmis à l'Urssaf. Cette action ne se défait pas depuis l'interface.<br/>Le ${prestataireWord} sera prévenu par email et dans ses notifications.`,
            input: "textarea",
            inputLabel: `Motif (facultatif, communiqué au ${prestataireWord})`,
            inputPlaceholder: "Ex : cours déclaré en doublon…",
            showCancelButton: true,
            confirmButtonText: "Oui, refuser et annuler",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#d33",
        });
        if (!isConfirmed) return;
        setActionId(course.id);
        try {
            await TravelRepository.refuseCourse(course.id, reason?.trim() || undefined);
            await loadReviews();
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Le refus a échoué. Si une demande de paiement est déjà partie à l'Urssaf, l'annulation doit passer par l'Urssaf." });
        } finally {
            setActionId(null);
        }
    };

    const handleVerify = async (course: ReviewCourse) => {
        const { isConfirmed } = await Swal.fire({
            icon: "question",
            title: `Valider l'attestation ${getActivePrestationBrandConfig("", "du ", "de la ")} n°${course.id} ?`,
            html: `Vous confirmez avoir contrôlé l'attestation et ses justificatifs.<br/>Un email de confirmation sera envoyé au ${prestataireWord} et à l'adresse contact.`,
            showCancelButton: true,
            confirmButtonText: "Oui, valider",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#0054A3",
        });
        if (!isConfirmed) return;
        setActionId(course.id);
        try {
            await TravelRepository.verifyCourse(course.id);
            await loadReviews();
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erreur", text: "La validation a échoué." });
        } finally {
            setActionId(null);
        }
    };

    const askFlagNote = async (title: string, confirmButtonText: string, placeholder: string) => {
        const { value, isConfirmed } = await Swal.fire({
            title,
            input: "text",
            inputPlaceholder: placeholder,
            showCancelButton: true,
            confirmButtonText,
            cancelButtonText: "Annuler",
        });
        return { note: typeof value === "string" ? value.trim() : "", isConfirmed };
    };

    const handleResolveFlag = async (flag: ComplianceFlagAdmin) => {
        const { note, isConfirmed } = await askFlagNote(
            `Résoudre l'anomalie n°${flag.id} ?`,
            "Résoudre",
            "Note facultative (ex. : déplacement vérifié avec le prof)"
        );
        if (!isConfirmed) return;
        setActionId(flag.id);
        try {
            await TravelRepository.resolveComplianceFlag(flag.id, note || undefined);
            setFlags((prev) => prev.filter((f) => f.id !== flag.id));
        } catch (e) {
            setFlagsError("La résolution a échoué. Réessayez.");
        } finally {
            setActionId(null);
        }
    };

    const handleDismissFlag = async (flag: ComplianceFlagAdmin) => {
        const { note, isConfirmed } = await askFlagNote(
            `Ignorer l'anomalie n°${flag.id} ?`,
            "Ignorer",
            "Motif facultatif"
        );
        if (!isConfirmed) return;
        setActionId(flag.id);
        try {
            await TravelRepository.dismissComplianceFlag(flag.id, note || undefined);
            setFlags((prev) => prev.filter((f) => f.id !== flag.id));
        } catch (e) {
            setFlagsError("L'opération a échoué. Réessayez.");
        } finally {
            setActionId(null);
        }
    };

    const formatKm = (value: number | null) => (value != null ? `${value.toFixed(1)} km` : "-");

    const renderFlagCard = (flag: ComplianceFlagAdmin) => (
        <div key={flag.id} className="border border-gray-200 rounded-md p-4 bg-white shadow-sm space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={`font-bold ${classes.primaryText}`}>
                    Anomalie n°{flag.id} : distance inhabituelle
                    {flag.createdAt && (
                        <span className="font-normal text-gray-600">
                            {" "}: relevée le {moment(flag.createdAt).format("DD/MM/YYYY à HH:mm")}
                        </span>
                    )}
                </p>
                <Chip
                    size="small"
                    color={flag.status === "confirmed_by_user" ? "info" : "warning"}
                    label={flag.status === "confirmed_by_user"
                        ? `Déplacement confirmé par le ${prestataireWord}${flag.confirmedAt ? ` le ${moment(flag.confirmedAt).format("DD/MM/YYYY")}` : ""}`
                        : "À traiter"}
                />
            </div>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                ⚠️ {flag.message}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <p>
                    <span className="font-semibold">{capitalizeFirstLetter(prestataireWord)} :</span>{" "}
                    {flag.teachr ? `${flag.teachr.firstName} ${flag.teachr.lastName} (${flag.teachr.mail})` : "-"}
                </p>
                <p>
                    <span className="font-semibold">{capitalizeFirstLetter(getActiveClientBrandConfig())} :</span>{" "}
                    {flag.client ? `${flag.client.firstName} ${flag.client.lastName}` : "-"}
                </p>
                <p>
                    <span className="font-semibold">Mode déclaré :</span> {flag.travelModeLabel}
                </p>
                <p>
                    <span className="font-semibold">Distance estimée :</span> {formatKm(flag.distanceKm)}
                    <span className="text-gray-500"> (seuil {formatKm(flag.thresholdKm)}, trajet routier)</span>
                </p>
                <p>
                    <span className="font-semibold">Relevée :</span>{" "}
                    {flag.courseId ? `à la déclaration ${lePrestation}n°${flag.courseId}` : "au choix du mode de déplacement"}
                </p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
                <ButtonField
                    variant="outlined"
                    label="Ignorer"
                    disabled={actionId === flag.id}
                    onClick={() => handleDismissFlag(flag)}
                />
                <ButtonField
                    variant="contained"
                    label="Résoudre"
                    loading={actionId === flag.id}
                    disabled={actionId === flag.id}
                    onClick={() => handleResolveFlag(flag)}
                />
            </div>
        </div>
    );

    const renderCourseCard = (course: ReviewCourse, mode: "approve" | "verify") => (
        <div key={course.id} className="border border-gray-200 rounded-md p-4 bg-white shadow-sm space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={`font-bold ${classes.primaryText}`}>
                    {capitalizeFirstLetter(prestationWord)} n°{course.id}
                    {course.startDatetime && (
                        <span className="font-normal text-gray-600">
                            {" "}: {moment(course.startDatetime).format("dddd DD MMMM YYYY à HH:mm")}
                        </span>
                    )}
                </p>
                <Chip
                    size="small"
                    color={mode === "approve" ? "warning" : "info"}
                    label={mode === "approve"
                        ? (course.declarationStatusReason ? "À approuver (adresse identique)" : "À approuver (> 160 km)")
                        : "À vérifier (60–160 km)"}
                />
            </div>
            {course.declarationStatusReason && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    ⚠️ {course.declarationStatusReason}
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <p>
                    <span className="font-semibold">{capitalizeFirstLetter(prestataireWord)} :</span>{" "}
                    {course.teachr ? `${course.teachr.firstName} ${course.teachr.lastName} (${course.teachr.mail})` : "—"}
                </p>
                <p>
                    <span className="font-semibold">{capitalizeFirstLetter(getActiveClientBrandConfig())} :</span>{" "}
                    {course.client ? `${course.client.firstName} ${course.client.lastName}` : "—"}
                </p>
                <p>
                    <span className="font-semibold">Adresse {getActiveClientBrandConfig("", "de l'", "du ")} :</span> {course.address ?? "—"}
                </p>
                <p>
                    <span className="font-semibold">Adresse du {prestataireWord} :</span> {course.teachrAddress ?? "—"}
                </p>
                <p>
                    <span className="font-semibold">Distance calculée :</span>{" "}
                    {course.travelDistanceKm != null ? `${parseFloat(course.travelDistanceKm).toFixed(1)} km` : "—"}
                </p>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
                {course.travelProof && (
                    <a href={course.travelProof.url} target="_blank" rel="noreferrer" className={`underline ${classes.primaryText}`}>
                        📎 Preuve de déplacement
                    </a>
                )}
                {mode === "approve" && !course.travelProof && !course.declarationStatusReason && (
                    <span className="text-red-500">Aucune preuve jointe pour le moment</span>
                )}
                {course.attestation && (
                    <>
                        <a href={course.attestation.url} target="_blank" rel="noreferrer" className={`underline ${classes.primaryText}`}>
                            📄 Attestation signée
                            {course.attestation.signedAt && ` le ${moment(course.attestation.signedAt).format("DD/MM/YYYY")}`}
                            {` (${course.attestation.travelModeLabel})`}
                        </a>
                        {course.attestation.supportingDocs.map((doc, index) => (
                            <a key={doc.path} href={doc.url} target="_blank" rel="noreferrer" className={`underline ${classes.primaryText}`}>
                                🧾 Justificatif {index + 1}
                            </a>
                        ))}
                    </>
                )}
                {mode === "verify" && !course.attestation && (
                    <span className="text-red-500">Attestation introuvable (peut-être invalidée par un changement d'adresse)</span>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
                {mode === "approve" ? (
                    <>
                        <ButtonField
                            variant="outlined"
                            label="Refuser"
                            disabled={actionId === course.id}
                            onClick={() => handleRefuse(course)}
                        />
                        <ButtonField
                            variant="outlined"
                            label="Demander un justificatif"
                            disabled={actionId === course.id}
                            onClick={() => handleReject(course)}
                        />
                        <ButtonField
                            variant="contained"
                            label="Approuver"
                            loading={actionId === course.id}
                            disabled={actionId === course.id}
                            onClick={() => handleApprove(course)}
                        />
                    </>
                ) : (
                    <>
                        <ButtonField
                            variant="outlined"
                            label="Refuser"
                            disabled={actionId === course.id}
                            onClick={() => handleRefuse(course)}
                        />
                        <ButtonField
                            variant="outlined"
                            label="Demander un justificatif"
                            disabled={actionId === course.id}
                            onClick={() => handleReject(course)}
                        />
                        <ButtonField
                            variant="contained"
                            label="Valider"
                            loading={actionId === course.id}
                            disabled={actionId === course.id}
                            onClick={() => handleVerify(course)}
                        />
                    </>
                )}
            </div>
        </div>
    );

    const currentList = tab === 0 ? toApprove : toVerify;

    return (
        <IndexApp
            index={1}
            teachr={teachr}
            component={
                <Layout
                    header={
                        <div className="px-1">
                            <BrandContent
                                tag="h1"
                                children="Gestion des déplacements revue des déclarations"
                                type="homeSubtitle"
                                className={`${common.subtitle} mb-2 ${classes.primaryText}`}
                            />
                            <BrandContent
                                tag="div"
                                children={`Déclarations soumises aux règles de distance : approuvez les ${getActivePrestationBrandConfig("s")} à plus de 160 km (preuve obligatoire) ou à adresse identique. Les attestations 60–160 km sont validées automatiquement à la signature — l'onglet « À vérifier » ne liste que les anciennes déclarations encore en attente.`}
                                type="description"
                                className={`lg:w-3/4 ${common.body} ${classes.text}`}
                            />
                        </div>
                    }
                >
                    <div className="border border-gray-200 shadow-sm rounded-md bg-white p-4 space-y-4">
                        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                            <Tab
                                label={
                                    <Badge badgeContent={toApprove.length} color="error" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                                        <span className="pr-3">À approuver</span>
                                    </Badge>
                                }
                            />
                            <Tab
                                label={
                                    <Badge badgeContent={toVerify.length} color="warning" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                                        <span className="pr-3">À vérifier</span>
                                    </Badge>
                                }
                            />
                            <Tab
                                label={
                                    <Badge badgeContent={flags.length} color="info" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                                        <span className="pr-3">Anomalies</span>
                                    </Badge>
                                }
                            />
                        </Tabs>

                        {error && <Alert severity="error">{error}</Alert>}
                        {loading && <p className={common.body}>Chargement…</p>}
                        {tab === 2 ? (
                            <>
                                <BrandContent
                                    tag="p"
                                    children={`Mode de déplacement incohérent avec la distance (à pied ou à vélo sur une longue distance). Ces anomalies ne bloquent jamais la déclaration : le ${prestataireWord} est invité à confirmer son déplacement, à vous de résoudre ou d'ignorer.`}
                                    type="description"
                                    className={`${common.body} ${classes.text}`}
                                />
                                {flagsError && <Alert severity="error">{flagsError}</Alert>}
                                {!loading && !flagsError && flags.length === 0 && (
                                    <Alert severity="success" variant="outlined">
                                        Aucune anomalie en attente. 🎉
                                    </Alert>
                                )}
                                <div className="space-y-4">
                                    {flags.map(renderFlagCard)}
                                </div>
                            </>
                        ) : (
                            <>
                                {!loading && !error && currentList.length === 0 && (
                                    <Alert severity="success" variant="outlined">
                                        Aucune déclaration en attente dans cette file. 🎉
                                    </Alert>
                                )}
                                <div className="space-y-4">
                                    {currentList.map((course) => renderCourseCard(course, tab === 0 ? "approve" : "verify"))}
                                </div>
                            </>
                        )}
                    </div>
                </Layout>
            }
        />
    );
};

export default AdminDeclarationReviewPage;
